/**
 * Trip Collection
 * Controller for the new-journey collection layout: group-chip navigation,
 * chip filters, and ajax Load More — all through the Section Rendering API.
 * Event delegation on document so swapped markup never needs re-binding.
 */

(function () {
  'use strict';

  var root = document.getElementById('TripCollection');
  if (!root || window.__tripCollectionInit) return;
  window.__tripCollectionInit = true;

  var sectionId = root.dataset.sectionId;

  function gridWrap() {
    return document.getElementById('ProductGridContainer');
  }

  function filtersPanel() {
    return document.getElementById('TripFilters');
  }

  /**
   * Panel placement: the filter panel renders in the section but lives in the
   * trip tool anchor so it opens above the pill row like the other modules.
   */
  function adoptFiltersPanel() {
    var panel = filtersPanel();
    var anchor = document.querySelector('trip-tool .trip-tool__stack');
    if (panel && anchor && panel.parentElement !== anchor) {
      anchor.appendChild(panel);
    }
  }

  function sectionUrl(base, params) {
    var url = new URL(base, window.location.origin);
    url.searchParams.set('section_id', sectionId);
    // Pin every fragment fetch to the country this page was rendered in.
    // Without this, a session whose context cookie failed to stick fetches
    // the DEFAULT market and appends Swiss products into a French grid
    var tool = document.querySelector('trip-tool');
    var iso = tool && tool.dataset.contextIso;
    if (iso) url.searchParams.set('country', iso.toUpperCase());
    if (params) {
      params.forEach(function (pair) {
        url.searchParams.append(pair[0], pair[1]);
      });
    }
    return url.toString();
  }

  function parseFetched(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  function setLoading(on) {
    var wrap = gridWrap();
    if (wrap) wrap.classList.toggle('is-loading', on);
  }

  /**
   * Card links carry the current filter params so the PDP can send the
   * shopper back to the collection exactly as they left it (market flow).
   *
   * Everything the card already carried is kept - above all `?variant=`,
   * which is how a colour tile opens the PDP on its own colour. Only
   * previously tagged filter params are dropped, so repeat calls (filter
   * change, load more, init) re-tag rather than stacking duplicates.
   */
  function tagCardLinks() {
    var search = new URLSearchParams(window.location.search);
    var params = [];
    search.forEach(function (value, key) {
      if (key.indexOf('filter.') === 0) params.push(encodeURIComponent(key) + '=' + encodeURIComponent(value));
    });
    var grid = document.getElementById('product-grid');
    if (!grid) return;
    grid.querySelectorAll('a[href*="/products/"]').forEach(function (link) {
      var href = link.getAttribute('href') || '';
      var mark = href.indexOf('?');
      var base = mark === -1 ? href : href.slice(0, mark);
      var kept = mark === -1 ? [] : href.slice(mark + 1).split('&').filter(function (pair) {
        return pair && pair.indexOf('filter.') !== 0;
      });
      var all = kept.concat(params);
      link.setAttribute('href', all.length ? base + '?' + all.join('&') : base);
    });
  }

  /**
   * Colour filtering is server-side: the section only renders colour tiles
   * whose variant is matched by the active storefront filters, so the
   * Search & Discovery colour groups decide - no client-side name matching.
   */

  /**
   * Grid balancer: while more pages remain, the sub-row remainder is hidden
   * and released when the next batch arrives; the true end may stay ragged.
   */
  function balanceGrid() {
    var grid = document.getElementById('product-grid');
    if (!grid) return;
    var tiles = Array.from(grid.children);
    tiles.forEach(function (tile) { tile.removeAttribute('hidden'); });
    var hasMore = Boolean(document.querySelector('[data-load-more]'));
    if (!hasMore || !tiles.length) return;
    var columns = getComputedStyle(grid).gridTemplateColumns.split(' ').length || 1;
    var remainder = tiles.length % columns;
    for (var i = tiles.length - remainder; i < tiles.length; i++) {
      tiles[i].setAttribute('hidden', '');
    }
  }

  /**
   * Full swap: replaces grid, header copy and the filter panel content from a
   * fetched render of this section (same collection or another one).
   */
  function swapFromDocument(doc) {
    var newRoot = doc.getElementById('TripCollection');
    var newPanel = doc.getElementById('TripFilters');
    if (newRoot) {
      var current = document.getElementById('TripCollection');
      current.innerHTML = newRoot.innerHTML;
    }
    var panel = filtersPanel();
    if (panel && newPanel) {
      panel.innerHTML = newPanel.innerHTML;
    }
    balanceGrid();
    tagCardLinks();
    updateFilterCount();
    document.dispatchEvent(new CustomEvent('theme:product-grid:rendered', { detail: { container: gridWrap() } }));
  }

  function fetchAndSwap(baseUrl, params, pushUrl) {
    setLoading(true);
    fetch(sectionUrl(baseUrl, params), { cache: 'no-cache' })
      .then(function (response) { return response.text(); })
      .then(function (html) {
        // Push the URL before swapping: tagCardLinks() reads the filter
        // params from location.search, so swapping first would tag the new
        // product links with the PREVIOUS chip's group.
        if (pushUrl) history.pushState({ tripCollection: true }, '', pushUrl);
        swapFromDocument(parseFetched(html));
      })
      .catch(function () {
        // Ajax failed - fall back to plain navigation so the user is never stuck
        window.location.assign(pushUrl || baseUrl);
      })
      .finally(function () { setLoading(false); });
  }

  /**
   * Applied-filter count on the Filters button. The panel's checked boxes
   * are the source: after any swap they carry the server-rendered state,
   * so the bubble never drifts from what the grid is actually filtered by.
   */
  function updateFilterCount() {
    var btn = document.querySelector('[data-filters-toggle]');
    var bubble = btn && btn.querySelector('[data-filters-count]');
    if (!bubble) return;
    var panel = filtersPanel();
    var count = panel ? panel.querySelectorAll('input[type="checkbox"]:checked').length : 0;
    bubble.textContent = count;
    bubble.hidden = count === 0;
    var clear = panel && panel.querySelector('[data-filters-clear]');
    if (clear) clear.hidden = count === 0;
    btn.classList.toggle('trip-tool__filters-btn--active', count > 0);
    var applied = count === 1
      ? btn.dataset.msgAppliedOne
      : (btn.dataset.msgAppliedMany || '').replace('[count]', count);
    // With no count the visible "Filters" label is the accessible name
    if (count > 0 && applied) btn.setAttribute('aria-label', applied);
    else btn.removeAttribute('aria-label');
  }

  function collectFilterParams() {
    var params = [];
    var panel = filtersPanel();
    if (!panel) return params;
    panel.querySelectorAll('input[type="checkbox"]:checked').forEach(function (input) {
      params.push([input.name, input.value]);
    });
    return params;
  }

  /**
   * The active group lives on the chip row, not in the panel - panel changes
   * must carry it or switching a filter would silently clear the group.
   */
  function activeGroupParam() {
    var chip = document.querySelector('[data-group-chip][data-param].trip-chip--active');
    return chip ? [[chip.dataset.param, chip.dataset.value]] : [];
  }

  function applyFilters() {
    var params = collectFilterParams().concat(activeGroupParam());
    var query = params.map(function (pair) {
      return encodeURIComponent(pair[0]) + '=' + encodeURIComponent(pair[1]);
    }).join('&');
    var pushUrl = window.location.pathname + (query ? '?' + query : '');
    fetchAndSwap(window.location.pathname, params, pushUrl);
  }

  /**
   * Events (delegated)
   */
  document.addEventListener('click', function (event) {
    var chip = event.target.closest && event.target.closest('[data-group-chip]');
    if (chip && !chip.hasAttribute('data-cross-collection')) {
      // TRUE-FILTER chip: applies the Group filter on THIS collection via a
      // light section render, composing with whatever the panel has checked.
      // The All chip carries no param and so clears the group.
      event.preventDefault();
      var params = collectFilterParams();
      if (chip.dataset.param) params.push([chip.dataset.param, chip.dataset.value]);
      var query = params.map(function (pair) {
        return encodeURIComponent(pair[0]) + '=' + encodeURIComponent(pair[1]);
      }).join('&');
      var pushUrl = window.location.pathname + (query ? '?' + query : '');
      fetchAndSwap(window.location.pathname, params, pushUrl);
      return;
    }
    if (chip) {
      event.preventDefault();
      var href = chip.getAttribute('href');
      // Cross-template: section ids differ per template, so fetch the full
      // page and extract the section markup instead of a section render.
      setLoading(true);
      fetch(href, { cache: 'no-cache' })
        .then(function (response) { return response.text(); })
        .then(function (html) {
          var doc = parseFetched(html);
          if (!doc.getElementById('TripCollection')) throw new Error('no trip layout');
          history.pushState({ tripCollection: true }, '', href);
          swapFromDocument(doc);
        })
        .catch(function () { window.location.assign(href); })
        .finally(function () { setLoading(false); });
      return;
    }

    var more = event.target.closest && event.target.closest('[data-load-more]');
    if (more) {
      event.preventDefault();
      more.classList.add('is-loading');
      fetch(sectionUrl(more.getAttribute('href')), { cache: 'no-cache' })
        .then(function (response) { return response.text(); })
        .then(function (html) {
          var doc = parseFetched(html);
          var grid = document.getElementById('product-grid');
          var newItems = doc.querySelectorAll('#product-grid > li:not(.trip-collection__empty)');
          newItems.forEach(function (item) { grid.appendChild(item); });

          var newMore = doc.querySelector('[data-load-more]');
          if (newMore) {
            more.setAttribute('href', newMore.getAttribute('href'));
            more.classList.remove('is-loading');
          } else {
            more.parentElement.remove();
          }
          balanceGrid();
          tagCardLinks();
        })
        .catch(function () {
          window.location.assign(more.getAttribute('href'));
        });
    }
  });

  // Panel header: Clear all unticks every filter and re-renders; the close
  // button dismisses the panel like a scrim tap (CIRKEL-262)
  document.addEventListener('click', function (event) {
    if (!event.target.closest) return;
    var tool = document.querySelector('trip-tool');
    if (event.target.closest('[data-filters-clear]')) {
      var panel = filtersPanel();
      if (panel) {
        panel.querySelectorAll('input[type="checkbox"]:checked').forEach(function (input) { input.checked = false; });
      }
      updateFilterCount();
      applyFilters();
      return;
    }
    if (event.target.closest('[data-filters-close]') && tool && tool.close) tool.close();
  });

  document.addEventListener('change', function (event) {
    if (event.target.closest && event.target.closest('#TripFilters')) {
      // Count first: the bubble answers the tick immediately, the grid
      // catches up when the render lands
      updateFilterCount();
      applyFilters();
    }
  });

  window.addEventListener('popstate', function () {
    // Back/forward: full-page fetch (the restored URL may be another template)
    setLoading(true);
    fetch(window.location.href, { cache: 'no-cache' })
      .then(function (response) { return response.text(); })
      .then(function (html) {
        var doc = parseFetched(html);
        if (!doc.getElementById('TripCollection')) throw new Error('no trip layout');
        swapFromDocument(doc);
      })
      .catch(function () { window.location.reload(); })
      .finally(function () { setLoading(false); });
  });

  adoptFiltersPanel();
  balanceGrid();
  tagCardLinks();
  updateFilterCount();

  // Column count changes across breakpoints - rebalance after resizes settle
  var balanceTimer;
  window.addEventListener('resize', function () {
    window.clearTimeout(balanceTimer);
    balanceTimer = window.setTimeout(balanceGrid, 200);
  });

  // Re-adopt after theme editor swaps (section reload re-renders the panel)
  document.addEventListener('shopify:section:load', adoptFiltersPanel);
})();
